// WindowRegistry — single home for floating-window mechanics (drag, resize,
// close, persistence, z-stack). Wraps the lower-level pointer machinery in
// `draggablePanels.ts` and adds two things the bare functions lack:
//   1. A cleanup contract: `register(...)` returns a handle whose `dispose()`
//      removes every listener wired by this module. Callers collect handles
//      into the same `offs[]` array used by other UI install* drivers.
//   2. localStorage-backed position + size persistence keyed by window id, so
//      a dragged console returns to its last spot on reload.
//
// Install pattern (matches the rest of `src/ui/`):
//   const wm = installWindowRegistry();   // → { registry, dispose }
//   wm.registry.register({ id: 'console', panel, header, ... });
//   offs.push(wm.dispose);

import {
    makeFreelyDraggable,
    makeResizableFromCorner,
    installFreeResizeClamp,
    raisePane,
    clampFreePanelByHandle,
} from '../draggablePanels';

const LS_PREFIX = 'magviz/window/';

export interface PersistedRect {
    readonly left?: number;
    readonly top?: number;
    readonly width?: number;
    readonly height?: number;
}

function lsKey(id: string): string {
    return `${LS_PREFIX}${id}`;
}

/** Exported for tests. Reads a previously-saved rect for `id`, or null. */
export function loadRect(id: string): PersistedRect | null {
    try {
        const raw = localStorage.getItem(lsKey(id));
        if (!raw) return null;
        // `JSON.parse` is typed `any`; narrow through `unknown` before any field access.
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') return null;
        // Narrowed to a plain object by the guard above; index reads return `unknown`.
        const r = parsed as Record<string, unknown>;
        const out: { left?: number; top?: number; width?: number; height?: number } = {};
        if (typeof r.left === 'number' && Number.isFinite(r.left)) out.left = r.left;
        if (typeof r.top === 'number' && Number.isFinite(r.top)) out.top = r.top;
        if (typeof r.width === 'number' && Number.isFinite(r.width)) out.width = r.width;
        if (typeof r.height === 'number' && Number.isFinite(r.height)) out.height = r.height;
        return out;
    } catch {
        return null;
    }
}

/** Exported for tests. Merges `rect` into the persisted entry for `id`. */
export function saveRect(id: string, rect: PersistedRect): void {
    try {
        const prev = loadRect(id) ?? {};
        const merged = { ...prev, ...rect };
        localStorage.setItem(lsKey(id), JSON.stringify(merged));
    } catch {
        /* quota or private mode — silently ignore */
    }
}

/** Exported for tests. Removes the persisted entry for `id`. */
export function clearRect(id: string): void {
    try { localStorage.removeItem(lsKey(id)); } catch { /* ignore */ }
}

/** Apply a persisted rect as inline styles, clamped to viewport so an off-
 *  screen persisted position (e.g. from a wider monitor) is brought back into
 *  reach. Clears any centering transform so the rect fully controls layout. */
function applyPersistedRect(panel: HTMLElement, rect: PersistedRect): void {
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (typeof rect.width === 'number') panel.style.width = `${Math.max(80, Math.min(rect.width, W))}px`;
    if (typeof rect.height === 'number') panel.style.height = `${Math.max(80, Math.min(rect.height, H))}px`;
    if (typeof rect.left === 'number' || typeof rect.top === 'number') {
        // We need the actual size after width/height have been written — read
        // back from offsetWidth/Height (which include the just-set inline dims).
        const w = panel.offsetWidth;
        const h = panel.offsetHeight;
        if (typeof rect.left === 'number') {
            const clamped = Math.max(0, Math.min(rect.left, W - 40));
            panel.style.left = `${clamped}px`;
            panel.style.right = 'auto';
            // Suppress unused-but-needed read of `w` for clamping above (kept
            // for symmetry / future use); both `w` and `h` are used below.
            void w;
        }
        if (typeof rect.top === 'number') {
            const clamped = Math.max(0, Math.min(rect.top, H - 40));
            panel.style.top = `${clamped}px`;
            panel.style.bottom = 'auto';
            void h;
        }
        panel.style.transform = 'none';
    }
}

export interface FloatingWindowOptions {
    /** Stable id, used as the localStorage key for position/size. */
    readonly id: string;
    /** The window root. Visibility is controlled by toggling the `show` class. */
    readonly panel: HTMLElement;
    /** Drag handle (typically a header div). */
    readonly header: HTMLElement;
    /** Optional SE-corner resize handle. */
    readonly resize?: HTMLElement | null;
    /** Optional close button — clicking it hides the window. */
    readonly closeBtn?: HTMLElement | null;
    /** Minimum size for resize. Defaults: 240×160. */
    readonly minWidth?: number;
    readonly minHeight?: number;
    /** Called when the window's visibility changes. */
    readonly onVisibilityChange?: (visible: boolean) => void;
}

export interface WindowHandle {
    readonly id: string;
    show(): void;
    hide(): void;
    toggle(): void;
    isVisible(): boolean;
    raise(): void;
    /** Reset to default position/size (clears persisted rect + inline styles). */
    reset(): void;
    /** Remove all listeners wired by `register()`. Does not remove the element. */
    dispose(): void;
}

export interface WindowRegistry {
    register(opts: FloatingWindowOptions): WindowHandle;
    get(id: string): WindowHandle | null;
    list(): readonly WindowHandle[];
    showAll(): void;
    hideAll(): void;
}

export interface InstalledRegistry {
    registry: WindowRegistry;
    dispose: () => void;
}

/** Install the global window registry. Returns the registry plus a cleanup
 *  that disposes every registered window. */
export function installWindowRegistry(): InstalledRegistry {
    const handles = new Map<string, WindowHandle>();

    const registry: WindowRegistry = {
        register(opts) {
            // Idempotent re-register: dispose the prior handle so we don't
            // double-wire listeners on the same DOM element.
            const prior = handles.get(opts.id);
            if (prior) prior.dispose();
            const handle = registerOne(opts, handles);
            handles.set(opts.id, handle);
            return handle;
        },
        get(id) {
            return handles.get(id) ?? null;
        },
        list() {
            return Array.from(handles.values());
        },
        showAll() {
            handles.forEach((h) => h.show());
        },
        hideAll() {
            handles.forEach((h) => h.hide());
        },
    };

    const dispose = (): void => {
        handles.forEach((h) => h.dispose());
        handles.clear();
    };

    return { registry, dispose };
}

function registerOne(opts: FloatingWindowOptions, handles: Map<string, WindowHandle>): WindowHandle {
    const { id, panel, header } = opts;
    const minWidth = opts.minWidth ?? 240;
    const minHeight = opts.minHeight ?? 160;

    // Restore persisted geometry before wiring drag/resize so the first paint
    // is at the saved location. Skipped if the panel is hidden (display:none),
    // since rect math against a 0×0 panel produces nonsense — applied lazily
    // on first show below.
    let restored = false;
    const restoreIfReady = (): void => {
        if (restored) return;
        if (getComputedStyle(panel).display === 'none') return;
        const saved = loadRect(id);
        if (saved) applyPersistedRect(panel, saved);
        restored = true;
    };
    restoreIfReady();

    const offs: Array<() => void> = [];

    // Double-click-to-maximize state. A genuine drag/resize cancels it (via the
    // onCommit/onReset hooks below) so the next double-click maximizes afresh.
    let maximized = false;
    let savedGeom: Record<'left' | 'top' | 'right' | 'bottom' | 'transform' | 'width' | 'height', string> | null = null;
    const cancelMaximize = (): void => {
        if (!maximized) return;
        maximized = false;
        savedGeom = null;
        panel.classList.remove('maximized');
    };

    // Drag → persist new top/left after the drag commits.
    offs.push(makeFreelyDraggable(panel, header, {
        onCommit: (rect) => { saveRect(id, { left: rect.left, top: rect.top }); cancelMaximize(); },
    }));

    // SE-corner resize → persist new width/height. Dblclick-reset clears the
    // size keys (top/left preserved — matches the underlying gesture behavior).
    if (opts.resize) {
        offs.push(makeResizableFromCorner(panel, opts.resize, {
            minWidth,
            minHeight,
            onCommit: (size) => { saveRect(id, { width: size.width, height: size.height }); cancelMaximize(); },
            onReset: () => {
                cancelMaximize();
                const prev = loadRect(id) ?? {};
                const next: { left?: number; top?: number } = {};
                if (prev.left !== undefined) next.left = prev.left;
                if (prev.top !== undefined) next.top = prev.top;
                if (next.left === undefined && next.top === undefined) clearRect(id);
                else saveRect(id, next);
            },
        }));
    }

    // Double-click the header to toggle a near-fullscreen maximize; a second
    // double-click restores the pre-maximize geometry. Mirrors the help modal.
    const MAXIMIZE_MARGIN_PX = 14;
    const onHeaderDblClick = (e: MouseEvent): void => {
        if ((e.target as Element | null)?.closest('button, a, input, textarea, select, [data-no-drag]')) return;
        e.preventDefault();  // suppress default title word-selection
        const s = panel.style;
        if (maximized && savedGeom) {
            s.left = savedGeom.left; s.top = savedGeom.top;
            s.right = savedGeom.right; s.bottom = savedGeom.bottom;
            s.transform = savedGeom.transform;
            s.width = savedGeom.width; s.height = savedGeom.height;
            cancelMaximize();
            return;
        }
        savedGeom = { left: s.left, top: s.top, right: s.right, bottom: s.bottom, transform: s.transform, width: s.width, height: s.height };
        s.left = `${MAXIMIZE_MARGIN_PX}px`;
        s.top = `${MAXIMIZE_MARGIN_PX}px`;
        s.right = 'auto';
        s.bottom = 'auto';
        s.transform = 'none';
        s.width = `calc(100vw - ${MAXIMIZE_MARGIN_PX * 2}px)`;
        s.height = `calc(100vh - ${MAXIMIZE_MARGIN_PX * 2}px)`;
        maximized = true;
        panel.classList.add('maximized');
        raisePane(panel);
    };
    header.addEventListener('dblclick', onHeaderDblClick);
    offs.push(() => header.removeEventListener('dblclick', onHeaderDblClick));

    // Browser-resize clamp so the persisted position stays reachable when the
    // user shrinks the window between sessions.
    offs.push(installFreeResizeClamp(panel, header));

    // Close-button → hide.
    if (opts.closeBtn) {
        const closeBtn = opts.closeBtn;  // narrow the optional into a local
        const onClose = (): void => {
            panel.classList.remove('show');
        };
        closeBtn.addEventListener('click', onClose);
        offs.push(() => closeBtn.removeEventListener('click', onClose));
    }

    // Raise on any interaction so click-to-front works regardless of where the
    // user clicks inside the window. Capture phase + on the panel (not just
    // header) so it fires even when descendants stop propagation.
    const onPointerDown = (): void => raisePane(panel);
    panel.addEventListener('pointerdown', onPointerDown, { capture: true });
    offs.push(() =>
        // `removeEventListener`'s overload narrows the third arg to `boolean`;
        // the cast preserves the capture-phase identity needed to unwire.
        panel.removeEventListener('pointerdown', onPointerDown, { capture: true } as EventListenerOptions),
    );

    // Observe show/hide via class mutation. Fires onVisibilityChange + raises
    // + applies persisted geometry lazily (covers the "panel hidden at register
    // time, shown later" path). Mirrors the pattern previously in events.ts.
    let lastVisible = panel.classList.contains('show');
    const mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.type !== 'attributes' || m.attributeName !== 'class') continue;
            const visibleNow = panel.classList.contains('show');
            if (visibleNow && !lastVisible) {
                restoreIfReady();
                raisePane(panel);
                // Re-clamp in case viewport changed while hidden.
                clampFreePanelByHandle(panel, header);
            }
            if (visibleNow !== lastVisible) {
                lastVisible = visibleNow;
                if (opts.onVisibilityChange) opts.onVisibilityChange(visibleNow);
            }
        }
    });
    mo.observe(panel, { attributes: true, attributeFilter: ['class'] });
    offs.push(() => mo.disconnect());
    if (lastVisible) raisePane(panel);

    const handle: WindowHandle = {
        id,
        show() {
            panel.classList.add('show');
        },
        hide() {
            panel.classList.remove('show');
        },
        toggle() {
            panel.classList.toggle('show');
        },
        isVisible() {
            return panel.classList.contains('show');
        },
        raise() {
            raisePane(panel);
        },
        reset() {
            cancelMaximize();
            clearRect(id);
            panel.style.left = '';
            panel.style.top = '';
            panel.style.right = '';
            panel.style.bottom = '';
            panel.style.width = '';
            panel.style.height = '';
            panel.style.transform = '';
            restored = false;
        },
        dispose() {
            // Idempotent: each `off()` is safe to call once; subsequent calls
            // are no-ops because `offs` is cleared below.
            offs.forEach((off) => { try { off(); } catch { /* swallow */ } });
            offs.length = 0;
            handles.delete(id);
        },
    };
    return handle;
}
